import type { Area } from "./model";

// The one site menu (unified navigation spec §3). Order here is rail order
// and page-list order. Gates mirror the old per-app menus: plan features,
// Spatie permissions (HR, tracker.view) and tenant roles.
const ADMIN = "admin" as const;

export const REGISTRY: Area[] = [
  {
    id: "home", label: "Home", icon: "Dashboard", brand: "control",
    groups: [{
      items: [
        { id: "dashboard", label: "Dashboard", path: "/", icon: "Dashboard" },
        { id: "reports", label: "Reports", path: "/reports", icon: "TrendUp", feature: "reports" },
        { id: "forms", label: "Forms", path: "/forms", icon: "Form", feature: "forms" },
      ],
    }],
  },
  {
    id: "water", label: "Water", icon: "Drop", brand: "legionella", feature: "water_compliance",
    groups: [
      {
        label: "Daily",
        items: [
          { id: "water-dashboard", label: "Dashboard", path: "/legionella", icon: "Dashboard" },
          { id: "water-calendar", label: "Calendar", path: "/legionella/calendar", icon: "Calendar" },
          { id: "water-work-orders", label: "Work orders", path: "/legionella/work-orders", icon: "Wrench" },
          { id: "water-alerts", label: "Alerts", path: "/legionella/alerts", icon: "Bell" },
          { id: "water-schedules", label: "Schedules", path: "/legionella/schedules", icon: "Clock" },
        ],
      },
      {
        label: "Records",
        items: [
          {
            id: "water-clients-sites", label: "Clients & sites", path: "/legionella/clients", icon: "Building",
            tabs: [
              { label: "Clients", path: "/legionella/clients" },
              { label: "Sites", path: "/legionella/sites" },
              { label: "Locations", path: "/legionella/locations" },
            ],
          },
          { id: "water-systems", label: "Systems", path: "/legionella/systems", icon: "Drop" },
          { id: "water-schematic", label: "Schematic", path: "/legionella/schematic", icon: "Drop" },
          { id: "water-assets", label: "Assets", path: "/legionella/assets", icon: "Boxes" },
          { id: "water-ra", label: "Risk assessments", path: "/legionella/risk-assessments", icon: "Shield" },
          { id: "water-ncs", label: "Non-conformities", path: "/legionella/non-conformities", icon: "Form", badge: "water-ncs" },
          { id: "water-sar", label: "Action reports", path: "/legionella/site-action-reports", icon: "Form" },
          { id: "water-orphans", label: "Orphans", path: "/legionella/orphans", icon: "AlertTri", badge: "water-orphans" },
        ],
      },
      {
        items: [{
          id: "water-settings", label: "Settings", path: "/legionella/settings/asset-types", icon: "Settings",
          matches: ["/legionella/settings"],
          tabs: [
            { label: "Asset types", path: "/legionella/settings/asset-types", minRole: ADMIN },
            { label: "Programmes", path: "/legionella/settings/programmes", minRole: ADMIN },
            { label: "Compliance bands", path: "/legionella/settings/compliance-bands", minRole: ADMIN },
            { label: "Working days", path: "/legionella/settings/working-days", minRole: ADMIN },
            { label: "Reminders", path: "/legionella/settings/reminders", minRole: ADMIN },
            { label: "RA sections", path: "/legionella/settings/ra-sections", minRole: ADMIN },
            { label: "Thermometers", path: "/legionella/settings/thermometers", minRole: ADMIN },
          ],
        }],
      },
    ],
  },
  {
    id: "fleet", label: "Fleet", icon: "Truck", brand: "tracker", feature: "vehicle_tracking",
    groups: [
      {
        items: [
          { id: "fleet-dashboard", label: "Dashboard", path: "/vehicles", icon: "Dashboard", permission: "tracker.view" },
          { id: "fleet-live", label: "Live map", path: "/vehicles/live", icon: "Map", permission: "tracker.view" },
          { id: "fleet-trips", label: "Trips", path: "/vehicles/trips", icon: "Clock", permission: "tracker.view" },
          {
            id: "fleet-vehicles", label: "Vehicles", path: "/vehicles/list", icon: "Truck", permission: "tracker.view",
            tabs: [
              { label: "Vehicles", path: "/vehicles/list" },
              { label: "Maintenance", path: "/vehicles/maintenance" },
              { label: "Fuel", path: "/vehicles/fuel" },
              { label: "Documents", path: "/vehicles/documents" },
            ],
          },
          {
            id: "fleet-drivers", label: "Drivers", path: "/vehicles/drivers", icon: "User", permission: "tracker.view",
            tabs: [
              { label: "Drivers", path: "/vehicles/drivers" },
              { label: "Scorecards", path: "/vehicles/scorecards" },
            ],
          },
          {
            id: "fleet-routes", label: "Routes", path: "/routes", icon: "Route",
            tabs: [
              { label: "Planner", path: "/routes", permission: "tracker.view" },
              { label: "My routes", path: "/my-routes" },
            ],
          },
          { id: "fleet-geofences", label: "Geofences", path: "/vehicles/geofences", icon: "Layers", permission: "tracker.view" },
          {
            id: "fleet-reports", label: "Reports", path: "/vehicles/reports", icon: "TrendUp", permission: "tracker.view",
            tabs: [
              { label: "Reports", path: "/vehicles/reports" },
              { label: "Cost", path: "/vehicles/reports/cost" },
            ],
          },
        ],
      },
      {
        items: [{
          id: "fleet-settings", label: "Settings", path: "/vehicles/settings/trackers", icon: "Settings",
          permission: "tracker.view", matches: ["/vehicles/settings"],
          tabs: [
            { label: "Trackers", path: "/vehicles/settings/trackers" },
            { label: "API keys", path: "/vehicles/settings/api-keys" },
            { label: "Audit log", path: "/vehicles/settings/audit" },
          ],
        }],
      },
    ],
  },
  {
    id: "people", label: "People", icon: "User", brand: "hr", feature: "hr.core",
    groups: [
      {
        label: "Me",
        items: [{
          id: "hr-me", label: "Me", path: "/hr/leave/me", icon: "User",
          tabs: [
            { label: "My leave", path: "/hr/leave/me" },
            { label: "My time", path: "/hr/time" },
            { label: "My expenses", path: "/hr/expenses" },
          ],
        }],
      },
      {
        label: "Team",
        items: [
          { id: "hr-dashboard", label: "Dashboard", path: "/hr", icon: "Dashboard" },
          {
            id: "hr-people", label: "People", path: "/hr/people", icon: "Building",
            tabs: [
              { label: "People", path: "/hr/people", permission: "hr.employees.view_directory" },
              { label: "Org chart", path: "/hr/org-chart", permission: "hr.org_chart.view" },
            ],
          },
          { id: "hr-approvals", label: "Approvals", path: "/hr/approvals", icon: "Check", permission: "hr.changes.approve" },
          {
            id: "hr-leave", label: "Leave", path: "/hr/leave", icon: "Calendar",
            tabs: [
              { label: "Calendar", path: "/hr/leave" },
              { label: "Team approvals", path: "/hr/leave/team", permission: "hr.leave.view_team" },
            ],
          },
          { id: "hr-time", label: "Time", path: "/hr/time", icon: "Clock", permission: "hr.time.view_team" },
          { id: "hr-expenses", label: "Expenses", path: "/hr/expenses", icon: "Pricing", permission: "hr.expenses.view_team" },
          { id: "hr-performance", label: "Performance", path: "/hr/performance", icon: "TrendUp", permission: "hr.performance.view_team" },
          { id: "hr-training", label: "Training", path: "/hr/training", icon: "Layers", permission: "hr.training.view" },
        ],
      },
      {
        label: "HR admin",
        items: [
          { id: "hr-recruitment", label: "Recruitment", path: "/hr/recruitment", icon: "Send", permission: "hr.recruitment.view" },
          { id: "hr-onboarding", label: "Onboarding", path: "/hr/lifecycle", icon: "Onboard", permission: "hr.lifecycle.view" },
          { id: "hr-cases", label: "Cases", path: "/hr/cases", icon: "Shield", permission: "hr.cases.view" },
          { id: "hr-payroll", label: "Payroll", path: "/hr/payroll", icon: "Pricing", permission: "hr.payroll.view" },
        ],
      },
      {
        items: [{
          id: "hr-settings", label: "Settings", path: "/hr/settings/documents", icon: "Settings",
          matches: ["/hr/settings"],
          tabs: [
            { label: "Documents", path: "/hr/settings/documents", permission: "hr.documents.view" },
            { label: "Background checks", path: "/hr/settings/background-checks", permission: "hr.background_checks.view" },
            { label: "Import", path: "/hr/settings/import", permission: "hr.employees.import", minRole: ADMIN },
          ],
        }],
      },
    ],
  },
  {
    id: "support", label: "Support", icon: "Ticket", brand: "control", feature: "support_tickets",
    groups: [{
      items: [
        { id: "support-mine", label: "My tickets", path: "/support/my-tickets", icon: "Ticket", badge: "support-mine" },
        { id: "support-inbox", label: "Inbox", path: "/support/inbox", icon: "Mail", minRole: ADMIN, badge: "support-inbox" },
        { id: "support-vendor", label: "Jowcey support", path: "/support/vendor", icon: "ExternalLink", minRole: ADMIN },
      ],
    }],
  },
  {
    // Placeholder until the Applications spec is built; admin-only so the
    // menu doesn't advertise an empty page to everyone.
    id: "apps", label: "Apps", icon: "Download", brand: "control", minRole: ADMIN,
    groups: [{ items: [{ id: "apps-applications", label: "Applications", path: "/applications", icon: "Download" }] }],
  },
  {
    id: "admin", label: "Admin", icon: "Settings", brand: "control", pinBottom: true,
    groups: [
      {
        items: [
          { id: "admin-org", label: "Organisation", path: "/admin", icon: "Settings", minRole: ADMIN },
          { id: "admin-customers", label: "Portal customers", path: "/customers", icon: "Building", feature: "customer_portal", minRole: "manager" },
          { id: "admin-pricing", label: "Plans & pricing", path: "/pricing", icon: "Pricing", minRole: ADMIN },
        ],
      },
      {
        label: "Super admin",
        items: [
          { id: "super", label: "Super admin", path: "/super", icon: "Shield", superAdmin: true },
          { id: "super-plans", label: "Plans catalogue", path: "/super/plans", icon: "Pricing", superAdmin: true },
          { id: "horizon", label: "Queue health", path: "/horizon", icon: "Bolt", superAdmin: true, external: true },
          { id: "portal-preview", label: "Portal preview", path: "/portal-preview", icon: "Eye", superAdmin: true },
          { id: "system", label: "Design system", path: "/system", icon: "Layers", superAdmin: true },
        ],
      },
    ],
  },
];
